import React from 'react';
// import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Grid,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';

import formsBackground from '../assets/forms.webp';

const zocdocLink =
  'https://www.zocdoc.com/practice/aspire-bhp-179924?lock=true&isNewPatient=false&referrerType=widget';

const Form = () => (
  <>
    <Helmet>
      <title>
        Patient Forms | Aspire Behavioral Health | San Antonio, TX
      </title>

      <meta
        name="description"
        content="Access new patient paperwork, follow-up forms, controlled substance agreements, and other patient forms online at Aspire Behavioral Health."
      />
    </Helmet>

    {/* Hero Section */}
    <Box
      sx={{
        position: 'relative',

        minHeight: {
          xs: 520,
          sm: 580,
          md: 650,
          lg: 620,
        },

        py: {
          xs: 8,
          sm: 10,
          md: 12,
          lg: 10,
        },

        overflow: 'hidden',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        color: 'white',
      }}
    >
      {/* Background Image */}
      <Box
        component="img"
        src={formsBackground}
        alt="Patient paperwork at Aspire Behavioral Health"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center',
          zIndex: -2,
        }}
      />

      {/* Overlay */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.45)',
          zIndex: -1,
        }}
      />

      {/* Hero Content */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          px: 3,
          maxWidth: '850px',
        }}
      >
        <Typography
          variant="h2"
          sx={{
            fontWeight: 600,
            mb: 2,
            fontSize: { xs: '2.2rem', sm: '3.5rem' },
            lineHeight: 1.2,
          }}
        >
          Patient Forms & Paperwork
        </Typography>

        <Typography
          variant="h6"
          sx={{
            fontWeight: 300,
            lineHeight: 1.8,
            mb: 4,
            fontSize: { xs: '1rem', sm: '1.2rem' },
          }}
        >
          Complete your forms securely online before your appointment.
        </Typography>

        {/* Hero Buttons */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Button
            variant="contained"
            href="tel:2108669850"
            sx={{
              backgroundColor: '#e3f3c5',
              color: 'black',
              fontWeight: 600,
              px: 4,
              py: 1.3,
              borderRadius: '30px',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#d3eab0',
              },
            }}
          >
            Call 210-866-9850
          </Button>

          <Button
                        component="a"
                        href={zocdocLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="outlined"
                        sx={{
                          borderColor: 'white',
                          color: 'white !important',
                          fontWeight: 600,
                          px: 4,
                          py: 1.3,
                          borderRadius: '30px',
                          textTransform: 'none',
                          '&:visited': {
                            color: 'white',
                          },
                          '&:hover': {
                            borderColor: 'white',
                            backgroundColor: 'rgba(255,255,255,0.1)',
                            color: 'white',
                          },
                        }}
                      >
                        Schedule an Appointment
                      </Button>
        </Box>
      </Box>
    </Box>

    {/* Main Content */}
    <Box sx={{ textAlign: 'center', mx: 'auto', py: 8, px: 2, backgroundColor: 'white' }}>
      {/* Intro Text */}
      <Typography
        variant="body1"
        sx={{
          maxWidth: '850px',
          mx: 'auto',
          mb: 6,
          lineHeight: 1.9,
          color: 'text.secondary',
          fontSize: { xs: '1rem', sm: '1.05rem' },
        }}
      >
        Complete your paperwork online before your appointment to help make your
        visit smoother and more convenient. Our forms are secure, easy to
        complete, and available anytime.
      </Typography>

      {/* New Patient Forms */}
      <Box
        sx={{
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          backgroundColor: '#e3f3c5',
          mt: 5,
          p: { xs: 3, sm: 5 },
          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.15)',
          borderRadius: 4,
          maxWidth: '1000px',
          mx: 'auto',
        }}
      >
        <Typography
          variant="h5"
          fontWeight="bold"
          sx={{ color: 'black', mb: 2 }}
        >
          NEW PATIENT PAPERWORK
        </Typography>

        <Typography
          variant="body1"
          sx={{
            textAlign: 'center',
            width: { xs: '100%', sm: '85%' },
            lineHeight: 1.8,
            color: '#333',
            mt: 1,
          }}
        >
          If you are new to Aspire Behavioral Health and have not yet seen one
          of our providers, please complete the New Patient Forms below. Once
          submitted, our team will contact you within 12–24 hours to schedule
          your appointment.
        </Typography>

        <Button
          variant="contained"
          href="https://app.formdr.com/practice/NDIwNzM=/form/ZAlHhd2-m36A_AETfJ8qDMypuCh3D5l7"
          target="_blank"
          sx={{
            mt: 5,
            mb: 2,
            px: 5,
            py: 1.5,
            borderRadius: '30px',
            backgroundColor: 'white',
            color: '#3a4c17',
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: '0px 2px 8px rgba(0,0,0,0.08)',
            '&:hover': {
              backgroundColor: '#f5f5f5',
              boxShadow: '0px 4px 12px rgba(0,0,0,0.12)',
            },
          }}
        >
          New Patient Form
        </Button>
      </Box>

      {/* Miscellaneous Forms */}
      <Box
        sx={{
          mt: 5,
          backgroundColor: '#e3f3c5',
          p: { xs: 3, sm: 5 },
          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.15)',
          borderRadius: 4,
          maxWidth: '1000px',
          mx: 'auto',
        }}
      >
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            color: 'black',
            fontWeight: 'bold',
            mb: 4,
          }}
        >
          MISCELLANEOUS FORMS
        </Typography>

        <Grid container spacing={3} justifyContent="center">
          {[
            {
              label: 'Follow Up Form',
              link: 'https://app.formdr.com/practice/NDIwNzM=/form/NsjsEAivFBDF_zAsYMolF7zG7L5xDQIq',
            },
            {
              label: 'Controlled Substance Agreement',
              link: 'https://app.formdr.com/practice/NDIwNzM=/form/rezDKF4ZElaw2SaAchlcuSlB8NLs6c8a',
            },
            {
              label: 'Document Submission Form',
              link: 'https://app.formdr.com/practice/NDIwNzM=/form/Jx_S9V5TvHLq8JObKwCftst2jFzMRh80',
            },
            {
              label: 'Authorization to Release PHI',
              link: 'https://app.formdr.com/practice/NDIwNzM=/form/SJWkwSAHjHcBCv4JmyYK1WVYOOwLMTR7',
            },
          ].map((item) => (
            <Grid item xs={12} sm={6} key={item.label}>
              <Button
                fullWidth
                variant="contained"
                href={item.link}
                target="_blank"
                sx={{
                  py: 1.6,
                  borderRadius: '30px',
                  backgroundColor: 'white',
                  color: '#3a4c17',
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: '0px 2px 8px rgba(0,0,0,0.08)',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                    boxShadow: '0px 4px 12px rgba(0,0,0,0.12)',
                  },
                }}
              >
                {item.label}
              </Button>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Bottom CTA */}
      <Box sx={{ textAlign: 'center', mt: 8 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
          Need help with forms or scheduling?
        </Typography>

        <Box
          sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Button
            variant="contained"
            href="tel:2108669850"
            sx={{
              backgroundColor: '#e3f3c5',
              color: 'black',
              fontWeight: 600,
              px: 4,
              py: 1.3,
              borderRadius: '30px',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#d3eab0',
              },
            }}
          >
            Call 210-866-9850
          </Button>

          <Button
                          component="a"
                          href={zocdocLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="outlined"
                          sx={{
                            backgroundColor: 'transparent',
                            border: '2px solid #3a4c17',
                            color: '#3a4c17 !important',
                            fontWeight: 600,
                            px: 4,
                            py: 1.3,
                            borderRadius: '30px',
                            textTransform: 'none',
                            '&:visited': {
                              color: '#3a4c17',
                            },
                            '&:hover': {
                              backgroundColor: 'rgba(58,76,23,0.05)',
                              border: '2px solid #3a4c17',
                              color: '#3a4c17',
                            },
                          }}
                        >
                          Schedule an Appointment
                        </Button>
        </Box>
      </Box>
    </Box>
  </>
);

export default Form;